import * as THREE from 'three';
import { SENDA } from '@somnhold/content';
import { boxLayout, boxTrain, gondolaLayout, hexToWorld, quarryLayout, type BattleState, type BoxLayout, type GondolaLayout, type MapDef, type QuarryLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { std } from '../materials.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The cable across the abyss (docs/sendas/artifice.md, chapter 2, «El cablecarril»), for the artificer's path: the
 * Flank Cliff's two faces with the abyss between them, its mist far below; on the north face Grada, the quarrymen's
 * hamlet hanging from the wall (stone houses on its ledges, stairs, quarry hoists, kitchen smoke, its windows lit at
 * dusk), and over it all the Raja, a black crack from end to end with a dull red deep in it. That wall is the chapter's
 * stake: it shows from every place of the cable.
 *
 * - `quarry` (act 1): the hanging quarry on the far face: its floor of cut terraces pale under the wind, blocks of cut
 *   stone, the back wall stepped where it was quarried; on its east edge the dwarven crane, its timber arm out over the
 *   abyss with five red-banded perches (the far three only reached from the scaffold of planks under it), the hook and
 *   the piece of Tormo's arm hanging from it (swaying when the arm leans, its sweep marked on the scaffold), the car
 *   below, loaded a piece at a time; Dunna's winch.
 * - `gondola` (act 2): the car's open deck and its cabin (Dunna at the window), its rail posts with a red band (where to
 *   hold on), the cable over it; the world goes by: the two faces, the pylons with their saddles, Grada on the wall to
 *   the north at the crossing's middle. A tilt is warned by a red chevron on the deck pointing down the slope and the
 *   posts' bands pulsing; the deck leans as it tips.
 * - `winchHouse` (act 3, the test) and `cableStation` (the scenes): the winch house on the workshop's side, a platform
 *   of planks and dressed stone over the drop; the windmill on the west turning axle A; the gearbox: its iron bed, the
 *   five slots (A, B1 and B2 chained, C, R on its swing arm), each with its painted letter; the gears where they lie on
 *   the shelf, sit or are carried, turning their way at their speed, a red arrow on each; the drum on the east with the
 *   cable going out over the abyss, its dial; the clutch lever. A right train: a white flash down the train and the
 *   cable singing (its line shivering).
 * - `cableNight` (the campfire): the car hanging at the last pylon by night, Dunna's lamp, Grada's lights far below.
 * - `planTable` (the opening's cinematic): Nadd's table in the workshop with the plan unrolled on it (a line across an
 *   abyss, two faces, a quarry; the note in the margin), a lamp.
 *
 * Draw calls: the dressing (one merged mesh), the world going by (one more, the car's act), the mist (two planes), the
 * cable (one tube, two for the car's act), the crane's arm and its piece, the car's deck, the gears (one each, five or
 * six), their arrows, the marks (one instanced pool), the glow layer, the sky. No lights of its own: the board's are
 * repainted (HOURS). Nothing here walks the nightmares; the sim's events and its state are read once a tick.
 */

export const CABLE_PLACES = new Set(['quarry', 'gondola', 'winchHouse', 'cableStation', 'cableNight', 'planTable']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const WARM = new THREE.Color('#ffb48a');
const COLD = new THREE.Color('#dce8ff');
const SLATE = ['#3c434d', '#343a43', '#47505b', '#2c3139', '#262a31'];
const QUARRY = ['#666c74', '#5c626a', '#70767e', '#535960'];
const IRON = '#2c3036';
const WOOD = ['#4a4038', '#3a3430', '#56493e', '#2e2925'];
const HOUSE = ['#7c7f84', '#6c7076', '#868a8f', '#5f6369'];
const ROOF = ['#2b2f36', '#353a42', '#24282e'];

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light by the hour, out over the abyss (cold, the far wall in the haze), and inside the workshop (`inside`). */
const HOURS: Record<Hour | 'inside', { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  // (Over the abyss in the wind: an overcast cold light, the haze a slate blue; never the white of a fogged board.)
  day: { bg: '#5d6b80', fog: ['#5f6d82', 30, 120], hemi: ['#c9d3e2', '#22252c', 1.55], key: ['#f3efe9', 2.2], rim: ['#9fb6ea', 1.5], sky: ['#2e3c55', '#7f8ea4', '#e9ecf2'] },
  dawn: { bg: '#5c6880', fog: ['#646f86', 30, 120], hemi: ['#ccd4e2', '#22252c', 1.6], key: ['#ffe2d2', 2.4], rim: ['#a9bfee', 1.5], sky: ['#2a3550', '#8790a6', '#ffc8b2'] },
  dusk: { bg: '#3a445a', fog: ['#434d66', 30, 140], hemi: ['#c3cfe2', '#1b1e26', 2.4], key: ['#ffd8c6', 2.8], rim: ['#9ab4f0', 1.9], sky: ['#161d36', '#6a7594', '#ff8f6e'] },
  night: { bg: '#060910', fog: ['#0a0e17', 24, 110], hemi: ['#7487b0', '#0b0c10', 1.25], key: ['#a9bbe6', 1.0], rim: ['#7a96ff', 1.25], sky: ['#020409', '#0f1520', '#9fb2e2'] },
  inside: { bg: '#050608', fog: ['#07080b', 18, 52], hemi: ['#a3afc6', '#1a1412', 2.2], key: ['#d6deee', 1.7], rim: ['#ff7a5a', 0.75], sky: ['#000000', '#000000', '#000000'] },
};

/** The mist far down the abyss: slow, broken, darker as it goes; lit cold from above. */
const mistMat = (alpha: number, scale: number, base = 0): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    // `base`: a veil everywhere under the broken patches (the car's deep layers: patches alone, few and far, never showed).
    uniforms: { time: { value: 0 }, tint: { value: new THREE.Color('#c9d3df') }, alpha: { value: alpha }, scale: { value: scale }, base: { value: base } },
    transparent: true,
    depthWrite: false,
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform vec3 tint; uniform float alpha; uniform float scale; uniform float base; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        vec2 p = vXZ * scale;
        float c = n(p + vec2(time * 0.03, time * 0.01)) * 0.6 + n(p * 2.3 - vec2(time * 0.05, 0.0)) * 0.4;
        float a = max(smoothstep(0.42, 0.78, c), base * (0.55 + 0.45 * c)) * alpha;
        gl_FragColor = vec4(tint * (0.75 + c * 0.35), a);
      }`,
  });

/** A gear: a toothed solid with its hub and spokes cut, its rim bevelled (one geometry, one draw). */
const gearGeo = (r: number, teeth: number, depth: number): THREE.BufferGeometry => {
  const sh = new THREE.Shape();
  const inner = r * 0.84;
  for (let i = 0; i < teeth; i++) {
    const a0 = (i / teeth) * Math.PI * 2;
    const a1 = a0 + (Math.PI * 2) / teeth;
    const pts: [number, number][] = [
      [inner, a0],
      [r, a0 + (a1 - a0) * 0.22],
      [r, a0 + (a1 - a0) * 0.5],
      [inner, a0 + (a1 - a0) * 0.72],
    ];
    pts.forEach(([rr, a], k) => (i === 0 && k === 0 ? sh.moveTo(Math.cos(a) * rr, Math.sin(a) * rr) : sh.lineTo(Math.cos(a) * rr, Math.sin(a) * rr)));
  }
  sh.closePath();
  const hub = new THREE.Path();
  hub.absarc(0, 0, r * 0.14, 0, Math.PI * 2, true);
  sh.holes.push(hub);
  // Four windows between the spokes.
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2 + Math.PI / 4;
    const w = new THREE.Path();
    w.absarc(Math.cos(a) * r * 0.5, Math.sin(a) * r * 0.5, r * 0.18, 0, Math.PI * 2, true);
    sh.holes.push(w);
  }
  const g = new THREE.ExtrudeGeometry(sh, { depth, bevelEnabled: true, bevelThickness: depth * 0.25, bevelSize: r * 0.03, bevelSegments: 1, curveSegments: 6 });
  g.rotateX(-Math.PI / 2);
  return g;
};

/** A gear's radius on the board by its teeth (12 small, 36 the biggest), sized so neighbours on the box mesh. */
const gearR = (teeth: number): number => S * (0.3 + (teeth / 36) * 0.5);

interface GearView {
  g: THREE.Group;
  spin: THREE.Group;
  teeth: number;
  angle: number;
  pos: THREE.Vector3;
  tilt: number;
}

export class CableLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private outside: boolean;
  private open = new Set<string>();
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number; world?: boolean }[] = [];
  private smokes: THREE.Vector3[] = [];
  private puff = 0;
  private mists: THREE.ShaderMaterial[] = [];
  private last: BattleState | null = null;
  private v3 = new THREE.Vector3();
  // The world beyond the board that goes by (the car's act) and its lights.
  private world = new THREE.Group();
  private worldX = 0;
  // The crane.
  private Q: QuarryLayout | null = null;
  private arm: THREE.Group | null = null;
  private piece: THREE.Group | null = null;
  private loaded: THREE.Mesh[] = [];
  private swingMark: THREE.Mesh | null = null;
  private swingMat: THREE.MeshBasicMaterial | null = null;
  private perchMarks: THREE.Mesh[] = [];
  private perchMat: THREE.MeshBasicMaterial | null = null;
  private sway = 0;
  // The car.
  private Gd: GondolaLayout | null = null;
  private deck: THREE.Group | null = null;
  private lean = 0;
  private chevron: THREE.Mesh | null = null;
  private chevMat: THREE.MeshBasicMaterial | null = null;
  private postBands: THREE.Mesh[] = [];
  private bandMat: THREE.MeshStandardMaterial | null = null;
  private cableMesh: THREE.Mesh | null = null;
  private span = 0;
  // The winch house.
  private B: BoxLayout | null = null;
  private gears: GearView[] = [];
  private arrows: THREE.Mesh[] = [];
  private slotAt: THREE.Vector3[] = [];
  private mill: THREE.Group | null = null;
  private drum: THREE.Group | null = null;
  private lever: THREE.Group | null = null;
  private leverPull = 0;
  private sing = 0;
  private rightFlash = 0;
  private peckMark: THREE.Mesh | null = null;
  private peckMat: THREE.MeshBasicMaterial | null = null;
  private beatMark: THREE.Mesh | null = null;
  private beatMat: THREE.MeshBasicMaterial | null = null;
  private dialNeedle: THREE.Mesh | null = null;
  private dialWant: THREE.Mesh | null = null;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `cable-${place}`;
    this.outside = place !== 'planTable';
    this.group.add(this.world);
    const kit = new Kit();
    const far = new Kit();
    if (place === 'quarry') this.buildQuarry(kit, far, s);
    else if (place === 'gondola') this.buildGondola(kit, far, s);
    else if (place === 'winchHouse' || place === 'cableStation') this.buildWinch(kit, far, s);
    else if (place === 'cableNight') this.buildNight(kit, far);
    else this.buildTable(kit);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0.05, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'cable-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // The world beyond (the far faces, Grada, the pylons): no shadows, it is far.
    const farMesh = far.build(mat, 'cable-far');
    if (farMesh) {
      this.geos.push(farMesh.geometry);
      this.world.add(farMesh);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private texs: THREE.Texture[] = [];
  /** Kept to dispose with the layer (not `std()`'s: those are cached and shared, materials.ts). */
  private track<T extends THREE.Material | THREE.BufferGeometry | THREE.Texture>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else if (x instanceof THREE.Texture) this.texs.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0, world = false): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick, world });
  }

  /** The mist far down the abyss: two broken layers. */
  private mist(y: number, alpha: number, at = new THREE.Vector3(0, 0, 0)): void {
    for (const [dy, a, sc] of [
      [0, alpha, 0.05],
      [-9, alpha * 0.8, 0.032],
    ] as const) {
      const m = mistMat(a, sc);
      this.mists.push(this.track(m));
      const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(320, 320).rotateX(-Math.PI / 2)), m);
      plane.position.set(at.x, y + dy, at.z);
      plane.renderOrder = -2;
      plane.name = 'cable-mist';
      this.group.add(plane);
    }
  }

  /**
   * The abyss under the car, deep: five layers of mist, paler near and darker down (each its own drift), and the dark
   * of the bottom under them. (Two layers in the fog's grey read as one flat plane under the deck.)
   */
  private deepMist(): void {
    for (const [y, a, sc, shade] of [
      [-5, 0.3, 0.075, 1.05],
      [-10, 0.42, 0.055, 0.9],
      [-16, 0.5, 0.04, 0.74],
      [-24, 0.58, 0.03, 0.58],
      [-34, 0.66, 0.022, 0.44],
    ] as const) {
      const m = mistMat(a, sc * 3, 0.55);
      // (Its shade against the hour's mist colour, kept when the hour repaints them.)
      m.userData.shade = shade;
      this.mists.push(this.track(m));
      const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(320, 320).rotateX(-Math.PI / 2)), m);
      plane.position.set(0, y, 0);
      plane.renderOrder = -2;
      plane.name = 'cable-mist';
      this.group.add(plane);
    }
    const bottom = new THREE.Mesh(this.track(new THREE.PlaneGeometry(400, 400).rotateX(-Math.PI / 2)), this.track(new THREE.MeshBasicMaterial({ color: '#18202c', fog: false })));
    bottom.position.y = -46;
    bottom.renderOrder = -3;
    bottom.name = 'cable-bottom';
    this.group.add(bottom);
  }

  /**
   * What the north face shows between Grada's ledges and away from it: cracks down it, thin falls of meltwater, old
   * quarry scaffolds hung on it, chains, ledges with pale edges, now and then a lamp (the far kit: it goes by).
   */
  private wallDetail(k: Kit, x0: number, x1: number, z: number, top: number, gx: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const fr = (v: number): number => {
      const q = Math.sin(v * 91.7 + 3.3) * 43758.5453;
      return q - Math.floor(q);
    };
    for (let x = x0 + 2; x < x1; x += 2.4) {
      if (Math.abs(x - gx) < 9) continue;
      const f = fr(x);
      const g = fr(x * 1.7 + 5);
      // (In front of the columns: their prisms reach two units out of the face.)
      const zf = z + 2.4;
      // (In the band of the face the game's camera sees past the car: well under the cable, -10 to -26; higher up
      // it is above the top of the screen.)
      const y = -10 - g * 15;
      if (f < 0.16) {
        // A crack down the face: one line, each length leaning a little off the last (joined end to end).
        let cx = x;
        let cy = y + 3;
        for (let i = 0; i < 6; i++) {
          const a = Math.sin(i * 1.9 + x) * 0.3;
          const len = 1.6;
          k.add(box, '#0e1014', place(cx - Math.sin(a) * len * 0.5, cy - Math.cos(a) * len * 0.5, zf, 0, new THREE.Vector3(0.22, len + 0.1, 0.12), 0, a));
          cx -= Math.sin(a) * len;
          cy -= Math.cos(a) * len;
        }
      } else if (f < 0.36) {
        // A thin fall of meltwater from a lip, and the lip.
        k.add(box, '#59616c', place(x, y + 0.2, zf + 0.3, 0, new THREE.Vector3(1.6, 0.3, 1.0)));
        k.add(box, '#8e9aab', place(x, y - 7, zf + 0.55, 0, new THREE.Vector3(0.5, 14, 0.05)));
        k.add(box, '#c9d3df', place(x + 0.05, y - 4, zf + 0.58, 0, new THREE.Vector3(0.18, 8, 0.04)));
        k.add(box, '#dfe6ee', place(x, y, zf + 0.6, 0, new THREE.Vector3(0.7, 0.12, 0.3)));
      } else if (f < 0.66) {
        // An old quarry scaffold hung on the face: posts, two decks, a ladder.
        for (const dx of [-1.3, 1.3]) k.add(box, WOOD[1] as string, place(x + dx, y - 1.5, zf + 0.5, 0, new THREE.Vector3(0.2, 4.6, 0.2)));
        for (const dy of [0, -2.6]) {
          k.add(box, WOOD[2] as string, place(x, y + dy, zf + 0.8, 0, new THREE.Vector3(3.0, 0.16, 1.1)));
          k.add(box, '#7a7064', place(x, y + dy + 0.09, zf + 0.8, 0, new THREE.Vector3(3.0, 0.03, 1.1)));
          k.add(box, WOOD[0] as string, place(x, y + dy + 0.5, zf + 1.3, 0, new THREE.Vector3(3.0, 0.08, 0.08)));
        }
        k.add(box, WOOD[0] as string, place(x + 0.5, y - 1.3, zf + 1.05, 0, new THREE.Vector3(0.06, 2.6, 0.06), 0, 0.15));
        k.add(box, WOOD[0] as string, place(x + 0.8, y - 1.3, zf + 1.05, 0, new THREE.Vector3(0.06, 2.6, 0.06), 0, 0.15));
        if (g > 0.5) this.light(new THREE.Vector3(x - 0.6, y + 0.5, zf + 1.0), WARM, 1.2, 0.45, 1, true);
      } else if (f < 0.82) {
        // Chains hanging from iron rings, swinging long into the mist.
        for (const dx of [-0.3, 0.4]) {
          k.add(box, '#2a2e34', place(x + dx, y + 0.4, zf + 0.3, 0, new THREE.Vector3(0.22, 0.22, 0.22)));
          k.add(box, '#31353c', place(x + dx, y - 4, zf + 0.42, 0, new THREE.Vector3(0.05, 8.6 + dx * 4, 0.05)));
        }
      } else {
        // A ledge sticking out, its edge pale with frost.
        k.add(box, SLATE[2] as string, place(x, y, zf + 0.4, 0, new THREE.Vector3(2.6, 0.6, 1.4)));
        k.add(box, '#9ea8b4', place(x, y + 0.32, zf + 0.5, 0, new THREE.Vector3(2.5, 0.06, 1.3)));
      }
    }
    box.dispose();
  }

  /** A cable hanging between two points (a sag in it), as a tube. */
  private cableTube(a: THREE.Vector3, b: THREE.Vector3, sag: number, r = 0.05, color = '#1e2126'): THREE.Mesh {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 24; i++) {
      const t = i / 24;
      const p = a.clone().lerp(b, t);
      p.y -= Math.sin(t * Math.PI) * sag;
      pts.push(p);
    }
    const geo = this.track(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 48, r, 5, false));
    const m = new THREE.Mesh(geo, std(color, { roughness: 0.5, metalness: 0.4 }));
    m.castShadow = false;
    return m;
  }

  /** A timber pylon rising from the abyss to the cable: four legs braced, a saddle with its wheel on top. */
  private pylon(kit: Kit, x: number, z: number, top: number, low = -34): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const h = top - low;
    for (const [dx, dz] of [
      [-0.7, -0.5],
      [0.7, -0.5],
      [-0.7, 0.5],
      [0.7, 0.5],
    ] as const) {
      kit.add(box, WOOD[1] as string, place(x + dx * 1.6, low + h / 2, z + dz * 1.6, 0, new THREE.Vector3(0.22, h, 0.22), dz * 0.05, -dx * 0.05));
    }
    for (let y = low + 3; y < top - 0.5; y += 3.2) {
      const w = 1.6 + ((top - y) / h) * 0.6;
      kit.add(box, WOOD[3] as string, place(x, y, z - 0.8, 0, new THREE.Vector3(w * 1.6, 0.12, 0.12), 0, 0.5));
      kit.add(box, WOOD[3] as string, place(x, y, z + 0.8, 0, new THREE.Vector3(w * 1.6, 0.12, 0.12), 0, -0.5));
      kit.add(box, WOOD[0] as string, place(x, y + 1.6, z, 0, new THREE.Vector3(2.4, 0.12, 1.9)));
    }
    // The saddle: a beam across, an iron sheave on it the cable runs over, a red-painted cap.
    kit.add(box, WOOD[2] as string, place(x, top - 0.15, z, 0, new THREE.Vector3(2.8, 0.3, 0.5)));
    const sheave = new THREE.CylinderGeometry(0.42, 0.42, 0.14, 14);
    kit.add(sheave, IRON, place(x, top + 0.25, z, 0, 1, Math.PI / 2, 0));
    kit.add(box, '#7a2a22', place(x, top + 0.02, z, 0, new THREE.Vector3(0.6, 0.08, 0.6)));
    box.dispose();
    sheave.dispose();
    this.light(new THREE.Vector3(x + 1.2, top - 0.3, z), WARM, 1.4, 0.5, 1, true);
  }

  /**
   * The north face with Grada hanging from it and the Raja over it: the wall runs west to east along `z` from `x0` to
   * `x1` (its foot lost in the mist), Grada's ledges centred at `gx`; `lit`: windows lit (dusk and night). In the kit
   * `k` (the far one, so the car's act moves it), the lights and smokes in world points (moved with it).
   */
  private gradaWall(k: Kit, x0: number, x1: number, z: number, top: number, gx: number, lit: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const prism = new THREE.CylinderGeometry(0.5, 0.6, 1, 6);
    const low = -40;
    // The face: columns of slate, deeper and shallower, their tops ragged; strata across it.
    for (let x = x0; x <= x1; x += 1.7) {
      const j = Math.sin(x * 12.9) * 0.5 + 0.5;
      const j2 = Math.sin(x * 3.3 + 1.1) * 0.5 + 0.5;
      const t = top + j * 3 + Math.sin(x * 0.21) * 2.5;
      k.add(prism, SLATE[Math.floor(j2 * 4.99)] as string, place(x, (low + t) / 2, z - j * 1.2, j * 3, new THREE.Vector3(2.2, t - low, 2.4 + j2)));
      if (j > 0.55) k.add(prism, SLATE[(Math.floor(j * 5) + 2) % 5] as string, place(x + 0.6, t - 4 - j2 * 9, z + 0.9, j, new THREE.Vector3(1.4, 3 + j * 4, 1.2)));
    }
    for (const y of [top - 6, top - 13, top - 24, top - 31]) k.add(box, SLATE[3] as string, place((x0 + x1) / 2, y, z + 1.2, 0, new THREE.Vector3(x1 - x0, 0.35, 0.6)));
    // Grada: five ledges, stone houses on them, stairs between, quarry hoists, chimneys.
    const ledges = [top - 9, top - 13.5, top - 18, top - 22.5, top - 27];
    ledges.forEach((ly, li) => {
      const w = 16 - li * 1.4 + (li % 2) * 2;
      const cx = gx + (li % 2 ? 2.2 : -1.8);
      k.add(box, '#4f565f', place(cx, ly - 0.35, z + 1.6, 0, new THREE.Vector3(w, 0.7, 2.6)));
      k.add(box, '#3a4048', place(cx, ly - 1.1, z + 1.3, 0, new THREE.Vector3(w * 0.96, 0.9, 2.0)));
      // A parapet of piled stones on the lip.
      for (let x = cx - w / 2 + 0.4; x < cx + w / 2; x += 0.9) k.add(box, '#5c636b', place(x, ly + 0.12, z + 2.75, Math.sin(x) * 0.2, new THREE.Vector3(0.7, 0.26, 0.28)));
      // The houses: a box of grey stone, a dark slate roof in two slopes, a chimney; lit windows at dusk.
      const n = Math.floor(w / 2.1);
      for (let i = 0; i < n; i++) {
        const hx = cx - w / 2 + 1.1 + i * 2.05 + Math.sin(i * 7.1 + li) * 0.25;
        if (Math.sin(i * 3.7 + li * 2.3) > 0.72) continue;
        const hh = 1.0 + (Math.sin(i * 5.3 + li) * 0.5 + 0.5) * 0.7;
        const hw = 1.4 + (Math.sin(i * 2.1) * 0.5 + 0.5) * 0.4;
        const hz = z + 1.1;
        k.add(box, HOUSE[(i + li) % 4] as string, place(hx, ly + hh / 2, hz, 0, new THREE.Vector3(hw, hh, 1.2)));
        for (const s of [-1, 1]) k.add(box, ROOF[(i + li) % 3] as string, place(hx, ly + hh + 0.26, hz + s * 0.3, 0, new THREE.Vector3(hw + 0.18, 0.1, 0.78), s * 0.62, 0));
        if (i % 2 === 0) {
          k.add(box, '#4a4e55', place(hx + hw * 0.3, ly + hh + 0.6, hz - 0.2, 0, new THREE.Vector3(0.22, 0.6, 0.22)));
          this.smokes.push(new THREE.Vector3(hx + hw * 0.3, ly + hh + 1.0, hz - 0.2));
        }
        // A door and a window on the face toward the abyss.
        k.add(box, '#1d1f23', place(hx - hw * 0.2, ly + 0.35, hz + 0.61, 0, new THREE.Vector3(0.32, 0.6, 0.03)));
        k.add(box, lit ? '#e8b48e' : '#1d2026', place(hx + hw * 0.22, ly + hh * 0.6, hz + 0.61, 0, new THREE.Vector3(0.3, 0.26, 0.03)));
        if (lit) this.light(new THREE.Vector3(hx + hw * 0.22, ly + hh * 0.6, hz + 0.75), WARM, 0.9, 0.55, 0.6, true);
      }
      // The stair down to the next ledge, zigzagging on the face.
      if (li < ledges.length - 1) {
        const sx = cx + (li % 2 ? -w / 2 + 0.8 : w / 2 - 0.8);
        for (let st = 0; st < 9; st++) k.add(box, '#626870', place(sx + (li % 2 ? st * 0.18 : -st * 0.18), ly - 0.5 - st * 0.5, z + 1.6, 0, new THREE.Vector3(1.0, 0.16, 0.9)));
      }
      // A quarry hoist: a timber gallows over the lip, its rope going down.
      if (li % 2 === 0) {
        const px = cx + w / 2 - 2.2;
        k.add(box, WOOD[1] as string, place(px, ly + 1.4, z + 2.4, 0, new THREE.Vector3(0.16, 2.8, 0.16)));
        k.add(box, WOOD[0] as string, place(px, ly + 2.7, z + 3.0, 0, new THREE.Vector3(0.14, 0.14, 1.4)));
        k.add(box, '#a59a86', place(px, ly + 0.4, z + 3.6, 0, new THREE.Vector3(0.03, 4.6, 0.03)));
        k.add(box, '#6d6a64', place(px, ly - 1.9, z + 3.6, 0, new THREE.Vector3(0.6, 0.45, 0.5)));
      }
    });
    // The Raja: a black crack from end to end over Grada, jagged; a dull red deep in it.
    let y = top - 3.5;
    for (let x = x0; x < x1; x += 0.9) {
      const dy = Math.sin(x * 1.9) * 0.6 + Math.sin(x * 0.37) * 1.3;
      const yy = y + dy;
      const wid = 0.45 + (Math.sin(x * 0.7) * 0.5 + 0.5) * 0.7 + (Math.abs(x - gx) < 12 ? 0.35 : 0);
      k.add(box, '#06070a', place(x, yy, z + 1.45, 0, new THREE.Vector3(1.1, wid, 0.4), 0, Math.cos(x * 1.9) * 0.5));
      k.add(box, '#1a0a09', place(x, yy, z + 1.3, 0, new THREE.Vector3(0.9, wid * 0.4, 0.3), 0, Math.cos(x * 1.9) * 0.5));
      if (Math.floor(x / 0.9) % 3 === 0) this.light(new THREE.Vector3(x, yy, z + 1.7), '#a8301f', 1.1 + wid, 0.32, 0.25, true);
      y += Math.sin(x * 0.13) * 0.02;
    }
    box.dispose();
    prism.dispose();
  }

  /** The cells open on the board (the rest is the drop, cut out of the ground). */
  private setOpen(cells: { q: number; r: number }[]): void {
    for (const h of cells) this.open.add(`${h.q},${h.r}`);
  }

  private voidAt = (x: number, z: number): boolean => {
    if (!this.open.size) return false;
    const hr = Math.round(z / (S * 0.866));
    const hq = Math.round(x / S - hr / 2);
    // (A little slack round each cell: the board's tiles are a touch bigger than their centres' grid.)
    for (const [dq, dr] of [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, -1],
      [-1, 1],
    ] as const) {
      const k = `${hq + dq},${hr + dr}`;
      if (!this.open.has(k)) continue;
      const c = hexToWorld(hq + dq, hr + dr);
      const cx = c.x * K;
      const cz = c.y * K;
      if ((x - cx) ** 2 + (z - cz) ** 2 < (0.62 * S) ** 2) return false;
    }
    return true;
  };

  /** The drop cut out of the board's ground (and the ground beyond, and what stands on it). */
  private carve(terrain: TerrainBuild): void {
    const cut = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!pos) return;
      mesh.updateMatrixWorld(true);
      const mw = mesh.matrixWorld;
      const v = new THREE.Vector3();
      const idx = geo.index;
      const tri = (i: number): number => (idx ? idx.getX(i) : i);
      const n = idx ? idx.count : pos.count;
      const keep: number[] = [];
      for (let i = 0; i < n; i += 3) {
        let cx = 0;
        let cz = 0;
        for (let k = 0; k < 3; k++) {
          v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
          cx += v.x;
          cz += v.z;
        }
        const out = this.voidAt(cx / 3, cz / 3);
        if (idx) {
          if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
        } else if (out) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);
          pos.setXYZ(i + 1, x, y, z);
          pos.setXYZ(i + 2, x, y, z);
        }
      }
      if (idx) geo.setIndex(keep);
      pos.needsUpdate = true;
      geo.computeBoundingSphere();
    };
    cut(terrain.mesh);
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') o.visible = false;
    });
  }

  /** A pole with a red band near its top (what to hold on, what a perch is). */
  private redBand(color = '#c23a2a'): THREE.MeshStandardMaterial {
    return std(color, { roughness: 0.55, emissive: '#3a0d08' });
  }

  /* ---------- the crane ---------- */

  private buildQuarry(kit: Kit, far: Kit, s: BattleState): void {
    const L = quarryLayout(s.seed);
    this.Q = L;
    this.setOpen([...L.floor, ...L.scaffold]);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const R = this.map.radius;
    const open = new Set([...L.floor, ...L.scaffold].map((h) => `${h.q},${h.r}`));
    const blocks = new Set(L.blocks.map((h) => `${h.q},${h.r}`));
    // The back wall, quarried in steps: pale blocks in courses, the chisel's lines, rising as it goes back.
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > R) continue;
        const k = `${q},${r}`;
        if (open.has(k)) continue;
        const c = hexToWorld(q, r);
        if (c.x > 1200 && !blocks.has(k)) continue;
        const p = this.hex(q, r);
        const near = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]].some(([dq, dr]) => open.has(`${q + (dq as number)},${r + (dr as number)}`));
        const j = Math.sin(q * 12.9 + r * 78.2) * 0.5 + 0.5;
        if (blocks.has(k)) {
          // A block of cut stone left on the floor, a chisel line on it, a wedge in its seam.
          kit.add(box, QUARRY[Math.floor(j * 3.99)] as string, place(p.x, p.y + 0.42, p.z, j * 0.6, new THREE.Vector3(1.0 * S, 0.84, 0.82 * S)));
          kit.add(box, '#5d636b', place(p.x, p.y + 0.86, p.z, j * 0.6, new THREE.Vector3(0.9 * S, 0.04, 0.05)));
          kit.add(box, WOOD[0] as string, place(p.x + 0.3, p.y + 0.9, p.z, j * 0.6, new THREE.Vector3(0.12, 0.2, 0.12)));
          continue;
        }
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        // Low by the floor (the camera looks over them), the steps rising as the face goes back, darker as they rise.
        const tall = near ? 0.8 + j * 0.5 : 1.4 + Math.max(0, d - R + 3) * 0.6 + j * 0.7;
        for (let c2 = 0; c2 < 3; c2++) {
          const hh = tall / 3;
          const tone = new THREE.Color(QUARRY[(Math.floor(j * 4) + c2) % 4] as string).multiplyScalar(1 - c2 * 0.12);
          kit.add(box, tone, place(p.x + (c2 % 2) * 0.05, p.y + hh * (c2 + 0.5), p.z, Math.floor(j * 3) * 0.04, new THREE.Vector3(1.12 * S, hh - 0.04, 1.12 * S)));
        }
      }
    // The scaffold: planks out over the drop on posts going down into the mist, a rope rail.
    for (const h of L.scaffold) {
      const p = this.hex(h.q, h.r);
      for (let i = -1; i <= 1; i++) kit.add(box, WOOD[(h.q + i + 3) % 4] as string, place(p.x, p.y - 0.05, p.z + i * 0.34, 0, new THREE.Vector3(1.2 * S, 0.1, 0.3)));
      kit.add(box, WOOD[3] as string, place(p.x - 0.4, p.y - 0.18, p.z, 0, new THREE.Vector3(0.12, 0.14, 1.1 * S)));
      kit.add(box, WOOD[1] as string, place(p.x - 0.4, p.y - 3.5, p.z + 0.4, 0, new THREE.Vector3(0.12, 7, 0.12), 0.08, 0));
    }
    const edge = L.scaffold.filter((h) => h.r === 0);
    for (const h of edge) {
      const p = this.hex(h.q, h.r);
      kit.add(box, WOOD[0] as string, place(p.x, p.y + 0.45, p.z + 0.55, 0, new THREE.Vector3(0.08, 0.9, 0.08)));
      kit.add(box, '#a59a86', place(p.x, p.y + 0.85, p.z + 0.55, 0, new THREE.Vector3(1.2 * S, 0.03, 0.03)));
    }
    // The quarry's lip on the east (the floor's edge going down).
    for (let r = -R; r <= R; r++) {
      let ex = -1e9;
      for (const h of L.floor) if (h.r === r) ex = Math.max(ex, hexToWorld(h.q, h.r).x);
      if (ex < -1e8) continue;
      const lx = (ex + 600) * K;
      const z = r * 866 * K;
      kit.add(box, QUARRY[3] as string, place(lx + 0.2, -4, z, 0, new THREE.Vector3(0.7, 8, 0.88 * S + 0.1)));
      kit.add(box, SLATE[2] as string, place(lx + 0.9, -14, z + 0.2, 0.1, new THREE.Vector3(1.3, 12, 0.95 * S)));
    }
    // The crane: a timber A-frame on the foot, bound with iron, its arm out east to the hook.
    const f = this.ctx.at(L.foot.x, L.foot.y, 0);
    kit.add(box, '#4a4f57', place(f.x, f.y + 0.25, f.z, 0, new THREE.Vector3(1.6, 0.5, 1.6)));
    for (const dz of [-0.5, 0.5]) kit.add(box, WOOD[1] as string, place(f.x, f.y + 1.9, f.z + dz, 0, new THREE.Vector3(0.28, 3.4, 0.28), 0, dz > 0 ? 0.12 : -0.12));
    kit.add(box, IRON, place(f.x, f.y + 3.5, f.z, 0, new THREE.Vector3(0.5, 0.3, 1.3)));
    // Dunna's winch: a drum on a frame, two cranks, the rope going up to the arm.
    const wn = this.ctx.at(L.winch.x, L.winch.y, 0);
    for (const dz of [-0.4, 0.4]) kit.add(box, WOOD[0] as string, place(wn.x + 0.5, wn.y + 0.45, wn.z + dz, 0, new THREE.Vector3(0.14, 0.9, 0.14)));
    kit.add(cyl, '#56493e', place(wn.x + 0.5, wn.y + 0.7, wn.z, 0, new THREE.Vector3(0.5, 0.9, 0.5), Math.PI / 2, 0));
    const arm = new THREE.Group();
    arm.position.set(f.x, f.y + 3.6, f.z);
    const ak = new Kit();
    const len = (L.hook.x - L.foot.x) * K + 0.3;
    ak.add(box, WOOD[2] as string, place(len / 2, 0, -0.12, 0, new THREE.Vector3(len, 0.26, 0.2)));
    ak.add(box, WOOD[2] as string, place(len / 2, 0, 0.12, 0, new THREE.Vector3(len, 0.26, 0.2)));
    for (let x = 0.6; x < len; x += 0.9) ak.add(box, WOOD[3] as string, place(x, 0.0, 0, 0, new THREE.Vector3(0.1, 0.3, 0.42), 0, 0.6));
    for (let x = 0.4; x < len; x += 1.8) ak.add(box, IRON, place(x, 0, 0, 0, new THREE.Vector3(0.08, 0.34, 0.46)));
    // The stay rope from the frame's top to the tip.
    ak.add(box, '#a59a86', place(len / 2, 0.55, 0, 0, new THREE.Vector3(len, 0.025, 0.025), 0, -0.12));
    ak.add(cyl, IRON, place(len - 0.1, -0.15, 0, 0, new THREE.Vector3(0.36, 0.14, 0.36), Math.PI / 2, 0));
    const amat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true }));
    const am = ak.build(amat, 'cable-arm');
    if (am) {
      am.castShadow = true;
      this.geos.push(am.geometry);
      arm.add(am);
    }
    // The perches on the arm: a red band each (where they sit), the far three a darker red (only from the scaffold).
    this.perchMat = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.9 }));
    for (const pp of L.perches) {
      const band = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.16, 0.3, 0.5)), pp.x >= L.heavyX ? this.redBand('#8a1e14') : this.redBand());
      band.position.set((pp.x - L.foot.x) * K, 0.02, 0);
      arm.add(band);
      // A ring on the ground under it: where it is reached from.
      const ring = new THREE.Mesh(this.track(new THREE.RingGeometry(0.34, 0.44, 24).rotateX(-Math.PI / 2)), this.perchMat);
      const gp = this.ctx.at(pp.x, pp.y + 866, 0.04);
      ring.position.copy(gp);
      ring.visible = false;
      this.perchMarks.push(ring);
      this.group.add(ring);
    }
    this.group.add(arm);
    this.arm = arm;
    // The piece on the hook: a length of Tormo's arm, dark iron, riveted, a red seam (it reads from far).
    const piece = new THREE.Group();
    const iron = std('#3a3f47', { roughness: 0.55, metalness: 0.35, emissive: '#0c0e12' });
    const seam = std('#c23a2a', { roughness: 0.6, emissive: '#5a120a' });
    const rope = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.035, 2.2, 0.035)), std('#a59a86', { roughness: 0.9 }));
    rope.position.y = -1.1;
    piece.add(rope);
    const body = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.42, 0.55, 2.0, 8)), iron);
    body.rotation.z = Math.PI / 2;
    body.position.y = -2.5;
    piece.add(body);
    const band = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.47, 0.47, 0.12, 8)), seam);
    band.rotation.z = Math.PI / 2;
    band.position.set(0.3, -2.5, 0);
    piece.add(band);
    const cap = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.5, 8, 6)), iron);
    cap.position.set(-1.0, -2.5, 0);
    piece.add(cap);
    const hookP = this.ctx.at(L.hook.x, L.hook.y, 3.45);
    piece.position.copy(hookP);
    this.group.add(piece);
    this.piece = piece;
    // The car below the hook, hanging from its own cable going west across the abyss; the pieces lowered into it.
    const car = this.ctx.at(L.hook.x, L.hook.y + 200, -2.8);
    kit.add(box, WOOD[0] as string, place(car.x, car.y, car.z, 0, new THREE.Vector3(3.2, 0.25, 2.0)));
    for (const dz of [-1, 1]) kit.add(box, WOOD[2] as string, place(car.x, car.y + 0.35, car.z + dz, 0, new THREE.Vector3(3.2, 0.5, 0.1)));
    kit.add(box, '#5b6068', place(car.x + 1.9, car.y + 0.7, car.z, 0, new THREE.Vector3(0.8, 1.4, 2.0)));
    kit.add(box, IRON, place(car.x + 1.9, car.y + 2.2, car.z, 0, new THREE.Vector3(0.12, 1.8, 0.12)));
    for (let i = 0; i < 3; i++) {
      const m = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.35, 0.45, 1.6, 8)), iron);
      m.rotation.z = Math.PI / 2;
      m.position.set(car.x - 0.9 + i * 0.9, car.y + 0.45, car.z + (i - 1) * 0.15);
      m.visible = false;
      this.loaded.push(m);
      this.group.add(m);
    }
    this.group.add(this.cableTube(new THREE.Vector3(car.x + 1.9, car.y + 3.1, car.z), new THREE.Vector3(car.x + 60, car.y + 8, car.z - 30), 3, 0.06));
    // The swaying piece's sweep on the scaffold: a red disc that fills while it is warned.
    this.swingMat = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0, depthWrite: false }));
    const sw = new THREE.Mesh(this.track(new THREE.CircleGeometry((SENDA_SWING_R * K) as number, 28).rotateX(-Math.PI / 2)), this.swingMat);
    sw.position.copy(this.ctx.at(L.swingAt.x, L.swingAt.y, 0.06));
    sw.renderOrder = 4;
    this.group.add(sw);
    this.swingMark = sw;
    // Lanterns on the quarry's posts, a brazier by the winch.
    for (const [x, y] of [
      [-2600, -2600],
      [-3600, 1700],
      [0, 2600],
    ] as const) {
      const p = this.ctx.at(x, y, 0);
      kit.add(box, WOOD[1] as string, place(p.x, p.y + 0.8, p.z, 0, new THREE.Vector3(0.12, 1.6, 0.12)));
      kit.add(box, '#e8eef8', place(p.x, p.y + 1.6, p.z, Math.PI / 4, new THREE.Vector3(0.14, 0.18, 0.14)));
      this.light(new THREE.Vector3(p.x, p.y + 1.6, p.z), COLD, 1.6, 0.6, 1);
    }
    // The far face across the abyss to the north-east: the workshop's cliff with Grada and the Raja.
    this.gradaWall(far, 4, 52, -20, 3, 20, false);
    this.pylon(far, 40, -12, 2);
    this.mist(-16, 0.55);
    box.dispose();
    cyl.dispose();
  }

  /* ---------- the car over the abyss ---------- */

  private buildGondola(kit: Kit, far: Kit, s: BattleState): void {
    const L = gondolaLayout(s.seed);
    this.Gd = L;
    this.setOpen(L.deck);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const deck = new THREE.Group();
    const dk = new Kit();
    // The deck: oak planks across, iron straps; the cabin on the east with its window and its roof.
    const x0 = -3.6 * S;
    const x1 = 1.4 * S;
    for (let x = x0; x < x1; x += 0.32) dk.add(box, WOOD[Math.floor(Math.abs(Math.sin(x * 5.1)) * 3.99)] as string, place(x + 0.16, -0.06, 0, 0, new THREE.Vector3(0.3, 0.12, 2.5 * S)));
    for (const z of [-1.1, 0, 1.1]) dk.add(box, IRON, place((x0 + x1) / 2, -0.02, z * S, 0, new THREE.Vector3(x1 - x0, 0.03, 0.08)));
    dk.add(box, '#2a2622', place((x0 + x1) / 2, -0.35, 0, 0, new THREE.Vector3(x1 - x0 + 0.2, 0.5, 2.6 * S)));
    const cx = 2.0 * S;
    dk.add(box, '#5b6068', place(cx, 0.85, 0, 0, new THREE.Vector3(1.4 * S, 1.7, 2.4 * S)));
    dk.add(box, '#6a7078', place(cx, 1.75, 0, 0, new THREE.Vector3(1.6 * S, 0.14, 2.6 * S)));
    dk.add(box, '#e8b48e', place(cx - 0.85 * S, 1.0, 0, 0, new THREE.Vector3(0.04, 0.5, 0.9)));
    // Its boards and iron: planked sides, corner irons, a rail on the roof, the clan's old paint gone grey.
    for (let y = 0.25; y < 1.7; y += 0.3) for (const z of [-1.2 * S, 1.2 * S]) dk.add(box, '#4f545b', place(cx, y, z, 0, new THREE.Vector3(1.42 * S, 0.04, 0.03)));
    for (const [dx, dz] of [
      [-0.7, -1.2],
      [0.7, -1.2],
      [-0.7, 1.2],
      [0.7, 1.2],
    ] as const)
      dk.add(box, IRON, place(cx + dx * S, 0.85, dz * S, 0, new THREE.Vector3(0.1, 1.72, 0.1)));
    for (const z of [-0.6, 0.6]) dk.add(box, '#e8b48e', place(cx, 1.05, z * S * 2.0, 0, new THREE.Vector3(0.6, 0.36, 0.04)));
    dk.add(box, IRON, place(cx, 1.92, 0, 0, new THREE.Vector3(1.5 * S, 0.06, 0.06)));
    dk.add(box, '#1d1f23', place(cx - 0.85 * S, 0.55, 0.85, 0, new THREE.Vector3(0.04, 1.1, 0.6)));
    // The rear rail, and the rails' stanchions (the hand-holds): iron, a red band where to hold.
    dk.add(box, IRON, place(x0, 0.5, 0, 0, new THREE.Vector3(0.08, 0.08, 2.4 * S)));
    this.bandMat = this.track(new THREE.MeshStandardMaterial({ color: '#c23a2a', roughness: 0.5, emissive: '#3a0d08' }));
    for (const hp of L.holds) {
      const p = { x: hp.x * K, z: hp.y * K };
      dk.add(cyl, IRON, place(p.x, 0.55, p.z, 0, new THREE.Vector3(0.12, 1.1, 0.12)));
      const b = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.1, 0.1, 0.22, 10)), this.bandMat);
      b.position.set(p.x, 0.85, p.z);
      deck.add(b);
      this.postBands.push(b);
    }
    // The hanger: an iron frame from the cabin's roof up to the trolley on the cable.
    dk.add(box, IRON, place(cx, 2.6, 0, 0, new THREE.Vector3(0.14, 1.7, 0.14)));
    dk.add(box, IRON, place(cx, 3.45, 0, 0, new THREE.Vector3(1.2, 0.2, 0.3)));
    for (const dx of [-0.4, 0.4]) dk.add(cyl, '#4a4f57', place(cx + dx, 3.6, 0, 0, new THREE.Vector3(0.36, 0.12, 0.36), Math.PI / 2, 0));
    // Tormo's arms in the car: three dark iron pieces, a red seam on each.
    for (let i = 0; i < 3; i++) {
      dk.add(cyl, '#3a3f47', place(-2.6 * S + i * 0.95, 0.35, -0.7 + (i % 2) * 0.25, 0.1 * i, new THREE.Vector3(0.7, 1.4, 0.7), 0, Math.PI / 2));
      dk.add(cyl, '#a8301f', place(-2.6 * S + i * 0.95 + 0.25, 0.35, -0.7 + (i % 2) * 0.25, 0.1 * i, new THREE.Vector3(0.76, 0.1, 0.76), 0, Math.PI / 2));
    }
    const dmat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true }));
    const dm = dk.build(dmat, 'cable-deck');
    if (dm) {
      dm.castShadow = true;
      dm.receiveShadow = true;
      this.geos.push(dm.geometry);
      deck.add(dm);
    }
    // The tilt's chevron: a red arrow down the deck's slope (hidden but while warned or tipping).
    const sh = new THREE.Shape();
    sh.moveTo(-1.2, 0);
    sh.lineTo(0, -0.9);
    sh.lineTo(1.2, 0);
    sh.lineTo(1.2, 0.45);
    sh.lineTo(0, -0.45);
    sh.lineTo(-1.2, 0.45);
    sh.closePath();
    this.chevMat = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0, depthWrite: false }));
    const chev = new THREE.Mesh(this.track(new THREE.ShapeGeometry(sh).rotateX(-Math.PI / 2)), this.chevMat);
    chev.position.set(-1.0 * S, 0.05, 0);
    chev.renderOrder = 5;
    deck.add(chev);
    this.chevron = chev;
    deck.position.y = this.ctx.ground(0, 0);
    this.group.add(deck);
    this.deck = deck;
    // The cable over the car, east and west far out (its sag on the far side).
    const cy = deck.position.y + 3.7;
    this.cableMesh = this.cableTube(new THREE.Vector3(-90, cy + 3, 0), new THREE.Vector3(90, cy + 5, 0), 4, 0.07);
    this.group.add(this.cableMesh);
    // The world that goes by: the two faces at the crossing's ends, the pylons, Grada on the north wall, the mist.
    const G = SENDA_LEN;
    this.span = 120;
    const at = (t: number): number => -t * this.span;
    void G;
    // The quarry's face (behind, at the start) and the workshop's (ahead, at the end), each a wall across the abyss.
    // (Behind, west: the quarry left at the start; ahead, east: the workshop's face at the end. The world goes west.)
    for (const [x, sgn] of [
      [-at(0) - 12, -1],
      [-at(1) + 12, 1],
    ] as const) {
      for (let z = -30; z <= 30; z += 2.2) {
        const j = Math.sin(z * 7.7 + x) * 0.5 + 0.5;
        far.add(cyl, SLATE[Math.floor(j * 4.99)] as string, place(x + sgn * j * 1.5, -14, z, j, new THREE.Vector3(3.2, 44, 2.6)));
      }
      far.add(box, sgn < 0 ? (QUARRY[0] as string) : '#4f565f', place(x - sgn * 1.2, 7.8, 0, 0, new THREE.Vector3(3, 0.5, 8)));
    }
    // The north wall all along, Grada at the crossing's middle (lit at dusk: its windows).
    this.gradaWall(far, -12, this.span + 14, -26, 8, this.span * 0.46, true);
    this.wallDetail(far, -12, this.span + 14, -26, 8, this.span * 0.46);
    // The south side: the far edge of the abyss, low and broken in the haze.
    for (let x = -10; x < this.span + 10; x += 3.1) {
      const j = Math.sin(x * 3.1) * 0.5 + 0.5;
      far.add(cyl, SLATE[3 + (Math.floor(j * 2) % 2)] as string, place(x, -22, 32 + j * 4, j, new THREE.Vector3(4, 30 + j * 10, 3)));
    }
    // The pylons where the sim's are (by the way's ticks).
    for (const t of SENDA_PYLONS) this.pylon(far, t * this.span, 0, cy - 0.2 - deck.position.y);
    this.deepMist();
    box.dispose();
    cyl.dispose();
  }

  /* ---------- the winch house ---------- */

  private buildWinch(kit: Kit, far: Kit, s: BattleState): void {
    const L = boxLayout(s.seed);
    this.B = L;
    this.setOpen(L.floor);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 14);
    const R = this.map.radius;
    const open = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    // The workshop's rock on the west and north, dressed stone where the station's wall was built on it.
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > R) continue;
        const k = `${q},${r}`;
        if (open.has(k)) continue;
        const c = hexToWorld(q, r);
        if (c.x > 3600) continue;
        const near = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]].some(([dq, dr]) => open.has(`${q + (dq as number)},${r + (dr as number)}`));
        if (!near) continue;
        const p = this.hex(q, r);
        const j = Math.sin(q * 12.9 + r * 78.2) * 0.5 + 0.5;
        const t = c.y < -2000 || c.x < -5000 ? 2.6 + j * 0.8 : 1.1 + j * 0.5;
        kit.add(box, ['#4a5058', '#444a52', '#50565f'][Math.floor(j * 2.99)] as string, place(p.x, p.y + t * 0.5, p.z, 0, new THREE.Vector3(1.16 * S, t, 1.16 * S)));
        kit.add(box, '#30353c', place(p.x, p.y + t + 0.06, p.z, 0, new THREE.Vector3(1.22 * S, 0.12, 1.22 * S)));
      }
    // The platform's east edge: planks out to the drop, a rail, the beams under it.
    for (const h of L.floor) {
      const c = hexToWorld(h.q, h.r);
      if (c.x < 2400) continue;
      const p = this.hex(h.q, h.r);
      for (let i = -1; i <= 1; i++) kit.add(box, WOOD[(h.q + i + 4) % 4] as string, place(p.x, p.y - 0.04, p.z + i * 0.34, 0, new THREE.Vector3(1.2 * S, 0.08, 0.3)));
      kit.add(box, WOOD[1] as string, place(p.x + 0.3, p.y - 3, p.z, 0, new THREE.Vector3(0.18, 6, 0.18), 0, -0.3));
    }
    // The windmill on the west: a tower of stone, four sails of slatted oak, its shaft east to A.
    const mw = this.ctx.at(L.mill.x, L.mill.y, 0);
    kit.add(cyl, '#525860', place(mw.x - 0.2, mw.y + 1.6, mw.z, 0, new THREE.Vector3(1.6, 3.2, 1.6)));
    kit.add(box, '#2b2f36', place(mw.x - 0.2, mw.y + 3.4, mw.z, 0, new THREE.Vector3(1.4, 0.5, 1.4)));
    const mill = new THREE.Group();
    mill.position.set(mw.x + 0.75, mw.y + 3.2, mw.z);
    const sail = std('#c9c2b4', { roughness: 0.95, side: THREE.DoubleSide });
    const spar = std(WOOD[0] as string, { roughness: 0.9 });
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2;
      const arm = new THREE.Group();
      arm.rotation.x = a;
      const sp = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.1, 2.6, 0.1)), spar);
      sp.position.y = 1.3;
      arm.add(sp);
      const cloth = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.9, 2.0)), sail);
      cloth.rotation.y = Math.PI / 2;
      cloth.position.set(0.02, 1.5, 0.45);
      arm.add(cloth);
      mill.add(arm);
    }
    this.group.add(mill);
    this.mill = mill;
    // (The scenes have no box: the car stands docked where it is, by the people. Its slots' points are kept.)
    const bx = this.place === 'winchHouse' ? kit : new Kit();
    // The box: its iron bed, the slots with their letters (A, B, B, C, R) painted in red on the bed.
    const letters = ['A', 'B', 'B', 'C', 'R'];
    const bedC = this.ctx.at(-1200, 700, 0);
    bx.add(box, '#2a2e34', place(bedC.x, bedC.y + 0.03, bedC.z, 0, new THREE.Vector3(6.6 * S, 0.06, 3.4 * S)));
    bx.add(box, '#4c535c', place(bedC.x, bedC.y + 0.05, bedC.z, 0, new THREE.Vector3(6.4 * S, 0.04, 3.2 * S)));
    for (const dz of [-1.62, 1.62]) bx.add(box, '#6a5a44', place(bedC.x, bedC.y + 0.1, bedC.z + dz * S, 0, new THREE.Vector3(6.6 * S, 0.12, 0.12)));
    L.slots.forEach((h, i) => {
      const p = this.hex(h.q, h.r);
      this.slotAt.push(p);
      bx.add(cyl, '#2a2e34', place(p.x, p.y + 0.06, p.z, 0, new THREE.Vector3(1.0, 0.1, 1.0)));
      bx.add(cyl, '#7a6a52', place(p.x, p.y + 0.14, p.z, 0, new THREE.Vector3(0.62, 0.08, 0.62)));
      bx.add(cyl, '#8a7a60', place(p.x, p.y + 0.36, p.z, 0, new THREE.Vector3(0.22, 0.5, 0.22)));
      bx.add(cyl, i === 4 ? '#8a2c22' : '#4c535c', place(p.x, p.y + 0.58, p.z, 0, new THREE.Vector3(0.32, 0.06, 0.32)));
      // The letter: a canvas sprite on the bed beside it.
      const tex = letterTex(letters[i] as string);
      this.track(tex);
      const lm = this.track(new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
      const plate = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.62, 0.62).rotateX(-Math.PI / 2)), lm);
      plate.position.set(p.x - 0.78, p.y + 0.1, p.z + 0.55);
      if (this.place === 'winchHouse') this.group.add(plate);
    });
    // B1 and B2's chain: a loop of dark links between their shafts (they turn as one).
    const b1 = this.slotAt[1] as THREE.Vector3;
    const b2 = this.slotAt[2] as THREE.Vector3;
    for (const dz of [-0.2, 0.2]) {
      const len = b1.distanceTo(b2);
      for (let t = 0; t < len; t += 0.16) bx.add(box, '#1a1c20', place(b1.x + ((b2.x - b1.x) * t) / len, b1.y + 0.3, b1.z + dz, 0, new THREE.Vector3(0.1, 0.06, 0.06)));
    }
    // R's swing arm, out from beside B2 and C.
    const rp = this.slotAt[4] as THREE.Vector3;
    bx.add(box, '#4c535c', place(rp.x, rp.y + 0.2, rp.z + 0.6, 0.3, new THREE.Vector3(0.16, 0.1, 1.3)));
    // The shaft from the mill to A, and from C to the drum.
    const a0 = this.slotAt[0] as THREE.Vector3;
    kit.add(cyl, '#8a7a60', place((mw.x + a0.x) / 2, a0.y + 0.5, a0.z, 0, new THREE.Vector3(0.14, a0.x - mw.x, 0.14), 0, Math.PI / 2));
    const c0 = this.slotAt[3] as THREE.Vector3;
    const dr = this.ctx.at(L.drum.x, L.drum.y, 0);
    kit.add(cyl, '#8a7a60', place((c0.x + dr.x) / 2, c0.y + 0.5, c0.z, 0, new THREE.Vector3(0.14, dr.x - c0.x, 0.14), 0, Math.PI / 2));
    // The drum: in a frame of oak and iron, the cable wound on it going out east over the abyss.
    for (const dz of [-1.0, 1.0]) kit.add(box, WOOD[1] as string, place(dr.x, dr.y + 0.9, dr.z + dz, 0, new THREE.Vector3(0.5, 1.8, 0.24)));
    kit.add(box, IRON, place(dr.x, dr.y + 1.9, dr.z, 0, new THREE.Vector3(0.7, 0.2, 2.3)));
    const drum = new THREE.Group();
    drum.position.set(dr.x, dr.y + 1.0, dr.z);
    const dm = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.75, 0.75, 1.7, 18)), std('#4a4038', { roughness: 0.8 }));
    dm.rotation.x = Math.PI / 2;
    drum.add(dm);
    for (const dz of [-0.85, 0.85]) {
      const rim = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.9, 0.9, 0.08, 18)), std('#8a2c22', { roughness: 0.6 }));
      rim.rotation.x = Math.PI / 2;
      rim.position.z = dz;
      drum.add(rim);
    }
    for (let k = 0; k < 6; k++) {
      const st = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.08, 0.08, 1.6)), std('#1d1f23', {}));
      const a = (k / 6) * Math.PI * 2;
      st.position.set(Math.cos(a) * 0.77, Math.sin(a) * 0.77, 0);
      drum.add(st);
    }
    this.group.add(drum);
    this.drum = drum;
    // The cable out over the abyss: down to the car hanging below the platform's edge.
    const out = new THREE.Vector3(dr.x + 0.8, dr.y + 1.7, dr.z);
    this.cableMesh = this.cableTube(out, new THREE.Vector3(dr.x + 90, dr.y - 10, dr.z + 30), 7, 0.07);
    this.group.add(this.cableMesh);
    // The car below, its cabin and the arms, just over the lip (the scenes: at the platform).
    // (The scenes: docked on the platform's south-east planks, by the people; the test: hanging below the lip.)
    const scene = this.place === 'cableStation';
    const car = scene ? this.ctx.at(-1600, -1000, 0) : this.ctx.at(L.drum.x + 4200, L.drum.y + 600, -3.4);
    // (At the miniatures' scale in the scenes, behind the people; at the fight's under the lip.)
    const k = scene ? 0.55 : 1;
    kit.add(box, WOOD[0] as string, place(car.x, car.y + 0.1 * k, car.z, 0, new THREE.Vector3(3.4 * k, 0.24 * k, 2.2 * k)));
    kit.add(box, '#5b6068', place(car.x + 1.3 * k, car.y + 0.9 * k, car.z, 0, new THREE.Vector3(1.2 * k, 1.6 * k, 2.2 * k)));
    kit.add(box, '#6a7078', place(car.x + 1.3 * k, car.y + 1.75 * k, car.z, 0, new THREE.Vector3(1.4 * k, 0.12 * k, 2.4 * k)));
    kit.add(box, '#e8b48e', place(car.x + 0.68 * k, car.y + 1.0 * k, car.z, 0, new THREE.Vector3(0.04, 0.4 * k, 0.8 * k)));
    for (let i = 0; i < 3; i++) {
      kit.add(cyl, '#3a3f47', place(car.x + (-1.0 + i * 0.7) * k, car.y + 0.5 * k, car.z + (-0.4 + (i % 2) * 0.3) * k, 0, new THREE.Vector3(0.6 * k, 1.2 * k, 0.6 * k), 0, Math.PI / 2));
      kit.add(cyl, '#a8301f', place(car.x + (-0.8 + i * 0.7) * k, car.y + 0.5 * k, car.z + (-0.4 + (i % 2) * 0.3) * k, 0, new THREE.Vector3(0.66 * k, 0.08 * k, 0.66 * k), 0, Math.PI / 2));
    }
    // The dial by the drum and the clutch lever (not in the scenes: the car stands there).
    if (this.place === 'winchHouse') {
      // The dial: a disc, its needle (now) and its red mark (what it asks).
      const dial = this.ctx.at(L.drum.x - 600, L.drum.y - 1400, 0);
      kit.add(box, WOOD[1] as string, place(dial.x, dial.y + 0.55, dial.z, 0, new THREE.Vector3(0.12, 1.1, 0.12)));
      const face = new THREE.Mesh(this.track(new THREE.CircleGeometry(0.34, 24)), this.track(new THREE.MeshBasicMaterial({ color: '#e8eef8' })));
      face.position.set(dial.x, dial.y + 1.25, dial.z + 0.09);
      this.group.add(face);
      const needleM = this.track(new THREE.MeshBasicMaterial({ color: '#15161a' }));
      const needle = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.04, 0.28, 0.02).translate(0, 0.13, 0)), needleM);
      needle.position.set(dial.x, dial.y + 1.25, dial.z + 0.11);
      this.group.add(needle);
      this.dialNeedle = needle;
      const wantM = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.07, 0.1, 0.03).translate(0, 0.28, 0)), this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a' })));
      wantM.position.copy(needle.position);
      wantM.rotation.z = -dialAngle(L.want) * L.way;
      this.group.add(wantM);
      this.dialWant = wantM;
      // The clutch lever: a long iron lever on a quadrant, its grip red.
      const lv = this.hex(L.lever.q, L.lever.r);
      kit.add(box, '#2a2e34', place(lv.x, lv.y + 0.12, lv.z, 0, new THREE.Vector3(0.9, 0.24, 0.5)));
      const lever = new THREE.Group();
      lever.position.set(lv.x, lv.y + 0.25, lv.z);
      const bar = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.08, 1.3, 0.08).translate(0, 0.65, 0)), std('#4c535c', { metalness: 0.3 }));
      lever.add(bar);
      const grip = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.11, 10, 8)), std('#c23a2a', { emissive: '#3a0d08' }));
      grip.position.y = 1.32;
      lever.add(grip);
      lever.rotation.z = 0.5;
      this.group.add(lever);
      this.lever = lever;
    }
    // The gears where they lie on the shelf: a rack of oak along the south.
    const lie0 = this.hex(L.lie[0]!.q, L.lie[0]!.r);
    const lie1 = this.hex(L.lie[L.lie.length - 1]!.q, L.lie[L.lie.length - 1]!.r);
    kit.add(box, WOOD[2] as string, place((lie0.x + lie1.x) / 2, lie0.y + 0.18, lie0.z + 0.7, 0, new THREE.Vector3(lie1.x - lie0.x + 2.0, 0.08, 0.5)));
    for (const x of [lie0.x - 0.9, (lie0.x + lie1.x) / 2, lie1.x + 0.9]) kit.add(box, WOOD[1] as string, place(x, lie0.y + 0.5, lie0.z + 0.9, 0, new THREE.Vector3(0.12, 1.0, 0.12)));
    if (this.place === 'winchHouse') L.pieces.forEach((teeth, k) => {
      const gv = this.gearView(teeth, k);
      gv.pos.copy(this.lieAt(k));
      gv.g.position.copy(gv.pos);
      this.gears.push(gv);
      // Its arrow, over it when it turns.
      const arrow = new THREE.Mesh(this.track(arrowGeo()), this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.95, depthWrite: false })));
      arrow.renderOrder = 6;
      arrow.visible = false;
      this.arrows.push(arrow);
      this.group.add(arrow);
    });
    // La Picotera's peck mark (a red ring that closes) and her wing-beat's (the platform's edge pulsing).
    this.peckMat = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0, depthWrite: false }));
    const pm = new THREE.Mesh(this.track(new THREE.RingGeometry(0.62, 1.02, 30).rotateX(-Math.PI / 2)), this.peckMat);
    pm.renderOrder = 5;
    this.group.add(pm);
    this.peckMark = pm;
    this.beatMat = this.track(new THREE.MeshBasicMaterial({ color: '#ff8a6e', transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide }));
    const bm = new THREE.Mesh(this.track(new THREE.RingGeometry(6.6 * S, 7.0 * S, 64).rotateX(-Math.PI / 2)), this.beatMat);
    bm.position.copy(this.ctx.at(-500, 500, 0.08));
    bm.renderOrder = 5;
    this.group.add(bm);
    this.beatMark = bm;
    // Lanterns on the station's posts.
    for (const [x, y] of [
      [-4800, -1700],
      [2200, -2600],
      [-4300, 2600],
      [3300, 2600],
    ] as const) {
      const p = this.ctx.at(x, y, 0);
      kit.add(box, WOOD[1] as string, place(p.x, p.y + 0.9, p.z, 0, new THREE.Vector3(0.12, 1.8, 0.12)));
      kit.add(box, '#e8eef8', place(p.x, p.y + 1.8, p.z, Math.PI / 4, new THREE.Vector3(0.14, 0.18, 0.14)));
      this.light(new THREE.Vector3(p.x, p.y + 1.8, p.z), COLD, 1.6, 0.6, 1);
    }
    // Across the abyss to the east and north: the far face, the cable's pylons, Grada on the wall below to the north.
    this.gradaWall(far, -30, 40, -30, -2, 6, this.place === 'cableStation' && false);
    this.pylon(far, 22, 9, 3);
    this.mist(-15, 0.55);
    box.dispose();
    cyl.dispose();
  }

  /** A gear's view: its solid (by teeth, a pale bronze so it reads on the dark bed), its red hub, its teeth painted. */
  private gearView(teeth: number, k: number): GearView {
    const g = new THREE.Group();
    const spin = new THREE.Group();
    g.add(spin);
    const r = gearR(teeth);
    const shade = teeth === 12 ? '#c9b48e' : teeth === 18 ? '#b8a27c' : teeth === 24 ? '#a8926e' : '#9a8462';
    const geo = this.track(gearGeo(r, teeth === 12 ? 12 : teeth === 18 ? 15 : teeth === 24 ? 18 : 24, 0.14));
    const mat = std(shade, { roughness: 0.45, metalness: 0.3, emissive: '#1a140e' });
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = true;
    spin.add(m);
    const hub = new THREE.Mesh(this.track(new THREE.CylinderGeometry(r * 0.2, r * 0.2, 0.24, 10)), std('#8a2c22', { roughness: 0.6 }));
    hub.position.y = 0.08;
    spin.add(hub);
    // Its teeth painted on its face (a canvas number that turns with it).
    const tex = this.track(letterTex(String(teeth), '#15161a', 'transparent'));
    const num = new THREE.Mesh(this.track(new THREE.PlaneGeometry(r * 0.9, r * 0.9).rotateX(-Math.PI / 2)), this.track(new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })));
    num.position.set(0, 0.2, r * 0.5);
    spin.add(num);
    this.group.add(g);
    return { g, spin, teeth, angle: k * 0.7, pos: new THREE.Vector3(), tilt: 0 };
  }

  private lieAt(k: number): THREE.Vector3 {
    const L = this.B as BoxLayout;
    const h = L.lie[k] as { q: number; r: number };
    const p = this.hex(h.q, h.r);
    return new THREE.Vector3(p.x, p.y + 0.32, p.z + 0.5);
  }

  /* ---------- the scenes ---------- */

  private buildNight(kit: Kit, far: Kit): void {
    // The car hanging at the last pylon by night: the deck and cabin on the board's middle, Dunna's lamp, the pylon.
    this.setOpen([
      { q: -1, r: 0 },
      { q: 0, r: 0 },
      { q: 1, r: 0 },
      { q: -1, r: 1 },
      { q: 0, r: 1 },
      { q: 0, r: -1 },
      { q: 1, r: -1 },
    ]);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const p = this.ctx.at(0, 300, 0);
    // (The cabin west, behind the two from the scene's cameras; the abyss and Grada north, in front of them.)
    for (let x = -1.8; x < 2.0; x += 0.32) kit.add(box, WOOD[Math.floor(Math.abs(Math.sin(x * 5.1)) * 3.99)] as string, place(p.x + x, p.y - 0.06, p.z, 0, new THREE.Vector3(0.3, 0.12, 2.2)));
    kit.add(box, '#5b6068', place(p.x - 2.5, p.y + 0.85, p.z, 0, new THREE.Vector3(1.4, 1.7, 2.2)));
    kit.add(box, '#6a7078', place(p.x - 2.5, p.y + 1.75, p.z, 0, new THREE.Vector3(1.6, 0.14, 2.4)));
    kit.add(box, '#e8b48e', place(p.x - 1.78, p.y + 1.0, p.z, 0, new THREE.Vector3(0.04, 0.5, 0.8)));
    kit.add(box, IRON, place(p.x - 2.5, p.y + 2.6, p.z, 0, new THREE.Vector3(0.14, 1.7, 0.14)));
    for (const [dx, dz] of [
      [1.8, -1.0],
      [1.8, 1.0],
      [0.2, -1.0],
      [0.2, 1.0],
    ] as const) {
      kit.add(box, IRON, place(p.x + dx, p.y + 0.45, p.z + dz, 0, new THREE.Vector3(0.08, 0.9, 0.08)));
      kit.add(box, '#a8301f', place(p.x + dx, p.y + 0.75, p.z + dz, 0, new THREE.Vector3(0.11, 0.16, 0.11)));
    }
    // Dunna's lamp on the deck: the only warm thing up here.
    kit.add(box, IRON, place(p.x - 0.2, p.y + 0.15, p.z + 0.3, 0, new THREE.Vector3(0.2, 0.3, 0.2)));
    this.light(new THREE.Vector3(p.x - 0.2, p.y + 0.4, p.z + 0.3), WARM, 3.2, 0.9, 1);
    this.group.add(this.cableTube(new THREE.Vector3(-80, p.y + 6, p.z), new THREE.Vector3(80, p.y + 7, p.z), 4, 0.07));
    this.pylon(far, p.x - 6.5, p.z, p.y + 3.2);
    // Grada below to the north, lit: its windows down in the dark, in front of the two.
    this.gradaWall(far, -40, 40, -15, 4, -1, true);
    this.mist(-24, 0.35);
    box.dispose();
  }

  private buildTable(kit: Kit): void {
    // Nadd's table in the workshop: oak on trestles at the miniatures' scale, the plan unrolled on it, a lamp; the rock
    // round it dark. (Sized to the figures: a dwarf stands about a table's height and a half.)
    this.setOpen([]);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const t = this.ctx.at(0, 0, 0);
    const k = 0.42;
    kit.add(box, WOOD[2] as string, place(t.x, t.y + 0.78 * k, t.z, 0, new THREE.Vector3(3.0 * k, 0.1 * k, 1.6 * k)));
    for (const [dx, dz] of [
      [-1.3, -0.6],
      [1.3, -0.6],
      [-1.3, 0.6],
      [1.3, 0.6],
    ] as const)
      kit.add(box, WOOD[1] as string, place(t.x + dx * k, t.y + 0.39 * k, t.z + dz * k, 0, new THREE.Vector3(0.12 * k, 0.78 * k, 0.12 * k)));
    const tex = this.track(planTex());
    const plan = new THREE.Mesh(this.track(new THREE.PlaneGeometry(2.4 * k, 1.3 * k).rotateX(-Math.PI / 2)), this.track(new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95, color: '#b8b2a6' })));
    plan.position.set(t.x, t.y + 0.84 * k + 0.005, t.z);
    this.group.add(plan);
    // The plan's rolled ends, weighted with a hammer; the lamp on its corner.
    for (const dx of [-1.25, 1.25]) kit.add(cylOf(), '#cfc8ba', place(t.x + dx * k, t.y + 0.88 * k, t.z, 0, new THREE.Vector3(0.12 * k, 1.3 * k, 0.12 * k), Math.PI / 2, 0));
    kit.add(box, IRON, place(t.x - 0.4 * k, t.y + 0.9 * k, t.z - 0.45 * k, 0.4, new THREE.Vector3(0.5 * k, 0.08 * k, 0.1 * k)));
    kit.add(box, IRON, place(t.x + 1.1 * k, t.y + 0.95 * k, t.z + 0.5 * k, 0, new THREE.Vector3(0.14 * k, 0.24 * k, 0.14 * k)));
    this.light(new THREE.Vector3(t.x + 1.1 * k, t.y + 1.05 * k, t.z + 0.5 * k), WARM, 1.4, 0.7, 1);
    // The workshop's walls round it: dressed stone, chains hanging.
    for (let a = 0; a < Math.PI * 2; a += 0.32) {
      const x = Math.cos(a) * 3.4;
      const z = Math.sin(a) * 2.8;
      kit.add(box, ['#3a4048', '#343a42', '#40464f'][Math.floor((Math.sin(a * 9) * 0.5 + 0.5) * 2.99)] as string, place(t.x + x, t.y + 1.2, t.z + z, -a, new THREE.Vector3(1.3, 2.4, 0.6)));
    }
    for (const dx of [-1.6, 1.4]) kit.add(box, '#1c1f24', place(t.x + dx, t.y + 1.6, t.z - 1.4, 0, new THREE.Vector3(0.03, 1.6, 0.03)));
    box.dispose();
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.7 + v.z * 0.9) * Math.cos(v.z * 1.3 - v.x * 0.5);
        // The quarry's floor of cut stone, pale, its terraces' seams; the winch house's flags and planks.
        if (this.place === 'quarry') c.set(QUARRY[Math.floor(k * 3.99)] as string).multiplyScalar(0.82 + lum * 0.3);
        else c.set(['#5a6068', '#636a72', '#535961'][Math.floor(k * 2.99)] as string).multiplyScalar(0.82 + lum * 0.35);
        if (this.place === 'quarry' && Math.abs(((v.x / S) * 0.5) % 1) < 0.04) c.multiplyScalar(0.82);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    this.carve(terrain);
    // The car's own deck stands in for the board: the ground under it hidden too.
    if (this.place === 'gondola' || this.place === 'planTable' || this.place === 'cableNight') terrain.mesh.visible = false;
    if (terrain.fog) terrain.fog.group.visible = false;
    if (this.outside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint(hour);
  }

  paint(hour: Hour): void {
    const look = HOURS[this.outside ? hour : 'inside'];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.7, hour === 'dusk' ? 0.08 : 0.4, -0.4);
    const mist = hour === 'night' ? '#3a4458' : hour === 'dusk' ? '#8c8fa6' : '#c9d3df';
    for (const m of this.mists) (m.uniforms.tint as THREE.IUniform<THREE.Color>).value.set(mist).multiplyScalar((m.userData.shade as number | undefined) ?? 1);
  }

  /** How high a flyer of the cable is drawn over its usual (sitting on the crane's arm, the car's cable or roof, the drum). */
  lift(s: BattleState, id: number): number {
    const st = s.senda?.garg;
    const r = s.rules?.senda?.garg;
    if (!st || !r) return 0;
    const u = st.list.find((g) => g.id === id);
    if (!u) return 0;
    if (u.mode === 4) return 1.15;
    if (u.mode !== 1 && u.mode !== 0) return this.place === 'gondola' ? 0.5 : 0.4;
    const p = r.perches[u.perch];
    if (!p) return 0;
    const k = p.kind === 0 ? 2.75 : p.kind === 1 ? 1.15 : p.kind === 2 ? 2.85 : 0.0;
    if (u.mode === 1) return k;
    // Flying in: rising to its perch as it nears.
    return k * 0.7;
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const at = (h = 0.2): THREE.Vector3 => this.ctx.at(e.x, e.y, h);
      if (e.kind === 'perch') {
        this.ctx.vfx.burst(at(this.place === 'gondola' ? 2.8 : 2.6), { n: 6, color: '#8d959e', speed: 0.6, up: -0.4, size: 0.1, life: 10, gravity: 1 });
      } else if (e.kind === 'armTilt' && e.n === 1) {
        this.ctx.shake(0.3);
      } else if (e.kind === 'pieceDown') {
        this.ctx.shake(0.25);
        const m = this.loaded[(e.n ?? 1) - 1];
        if (m) {
          m.visible = true;
          this.ctx.vfx.burst(m.position.clone(), { n: 18, color: '#aab2bc', speed: 1.4, up: 1.2, size: 0.2, life: 14, smoke: true });
          this.ctx.flare(m.position.clone().setY(m.position.y + 0.6), '#ffffff', 2.2, 10);
        }
      } else if (e.kind === 'swing') {
        this.ctx.shake(0.2);
        this.ctx.vfx.ring(at(0.08), 0.4, 1.4, '#ff8a6e', 6, 0.8);
      } else if (e.kind === 'pieceDrop') {
        this.ctx.shake(0.7);
        if (this.piece) this.piece.userData.drop = 1;
      } else if (e.kind === 'carStop') {
        if (e.n === 1) this.ctx.shake(0.35);
      } else if (e.kind === 'tilt') {
        this.ctx.shake(0.55);
      } else if (e.kind === 'fell') {
        this.ctx.shake(0.8);
      } else if (e.kind === 'pylon' && e.n !== 9) {
        this.ctx.shake(0.2);
      } else if (e.kind === 'put' || e.kind === 'take') {
        this.ctx.vfx.burst(at(0.5), { n: 8, color: '#ffb48a', speed: 1.2, up: 0.8, size: 0.08, life: 8, gravity: 1 });
      } else if (e.kind === 'clutch') {
        this.leverPull = 1;
        const L = this.B;
        if (e.n === 0 && L) {
          // Right: the cable sings, a white flash down the train.
          this.sing = 1;
          this.rightFlash = 1;
          this.ctx.shake(0.4);
          this.ctx.flare(this.ctx.at(L.drum.x, L.drum.y, 1.4), '#ffffff', 4, 18);
          for (const p of this.slotAt) this.ctx.vfx.ring(p.clone().setY(p.y + 0.4), 0.3, 1.8, '#ffffff', 10, 0.9);
        } else if (e.n === 1 && L) {
          this.ctx.shake(0.9);
          this.ctx.vfx.burst(this.ctx.at(L.drum.x + 1500, L.drum.y, 0.4), { n: 30, color: '#ffb48a', speed: 3.4, up: 2, size: 0.12, life: 12, gravity: 1 });
        } else if (e.n === 2 || e.n === 3) {
          this.ctx.shake(0.25);
        } else this.ctx.shake(0.12);
      } else if (e.kind === 'knockOut') {
        this.ctx.shake(0.3);
        this.ctx.vfx.burst(at(0.5), { n: 20, color: '#ffb48a', speed: 3, up: 1.6, size: 0.1, life: 10, gravity: 1 });
      } else if (e.kind === 'peck') {
        this.ctx.shake(0.35);
        this.ctx.vfx.ring(at(0.08), 0.3, 1.4, '#ff4f3a', 6, 0.9);
        this.ctx.vfx.burst(at(0.2), { n: 10, color: '#8d959e', speed: 1.8, up: 1, size: 0.14, life: 8, smoke: true });
      } else if (e.kind === 'beat') {
        this.ctx.shake(0.7);
        this.ctx.vfx.ring(this.ctx.at(-500, 500, 0.1), 0.5, 9 * S, '#ff8a6e', 14, 0.7);
      } else if (e.kind === 'pickDown') {
        this.ctx.shake(0.8);
        this.ctx.vfx.burst(at(1.8), { n: 34, color: '#8d959e', speed: 2.6, up: 2.2, size: 0.3, life: 22, smoke: true });
      } else if (e.kind === 'bolt') {
        const by = s.senda?.npcs.find((n) => n.id === e.npc);
        if (by) this.ctx.vfx.beam(this.ctx.at(by.x, by.y, this.place === 'gondola' ? 1.1 : 1.0), at(this.place === 'gondola' ? 1.6 : 1.3), '#e8eef8', 2, 0.03);
      }
    }
  }

  draw(s: BattleState): void {
    this.last = s;
    // The crane: the pieces already in the car.
    const cw = s.senda?.crane;
    if (cw) this.loaded.forEach((m, i) => (m.visible = i < cw.done));
    if (this.Q && cw) {
      const gr = s.rules?.senda?.garg;
      // The rings under the perches show where one sits.
      this.perchMarks.forEach((m, i) => (m.visible = !!s.senda?.garg?.list.some((g) => g.mode === 1 && g.perch === i) && !!gr));
    }
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    for (const m of this.mists) (m.uniforms.time as THREE.IUniform<number>).value = now;
    const s = this.last;
    // The crane: the arm leans with the weight on it, the piece sways; dropped, it falls into the mist.
    const cw = s?.senda?.crane;
    if (this.arm && this.piece && cw) {
      const want = cw.sway >= 0 ? 1 : 0;
      this.sway += (want - this.sway) * Math.min(1, dt * 3);
      this.arm.rotation.z = -0.09 * this.sway;
      const sw = Math.sin(now * 2.6) * 0.42 * this.sway;
      this.piece.rotation.z = sw;
      if (this.piece.userData.drop) {
        this.piece.position.y -= dt * 9;
        this.piece.rotation.x += dt * 1.2;
      } else if (cw.end < 0) {
        // Down the rope a piece at a time: the hook lowering as Dunna winds.
        const r = s?.rules?.senda?.crane;
        const k = r ? cw.prog / r.lower : 0;
        const base = this.Q ? this.ctx.at(this.Q.hook.x, this.Q.hook.y, 3.45).y : 0;
        this.piece.position.y = base - k * 2.6;
        this.piece.visible = cw.done < 3;
      } else this.piece.visible = false;
      if (this.swingMat) {
        const r = s?.rules?.senda?.crane;
        const warned = cw.sway >= 0;
        const k = r && warned ? Math.min(1, cw.sway / Math.max(1, r.swing.warn)) : 0;
        this.swingMat.opacity = warned ? 0.18 + 0.32 * k + 0.12 * Math.sin(now * 8) : 0;
      }
      if (this.perchMat) this.perchMat.opacity = 0.6 + 0.3 * Math.sin(now * 5);
    }
    // The car: the world going by, the deck leaning in a tilt, the chevron down its slope, the posts' bands pulsing.
    const gw = s?.senda?.gondola;
    if (this.Gd && gw && s) {
      const r = s.rules?.senda?.gondola;
      const k = r ? gw.prog / r.len : 0;
      this.worldX += (-k * this.span - this.worldX) * Math.min(1, dt * 4);
      this.world.position.x = this.worldX;
      const tipping = gw.warn >= 0 && s.tick >= gw.tip;
      const warned = gw.warn >= 0;
      const target = tipping ? gw.side * 0.13 : warned ? gw.side * 0.03 * Math.sin(now * 9) : 0;
      this.lean += (target - this.lean) * Math.min(1, dt * 5);
      if (this.deck) this.deck.rotation.x = this.lean;
      this.world.rotation.x = -this.lean * 0.35;
      if (this.chevron && this.chevMat) {
        this.chevron.rotation.y = gw.side > 0 ? Math.PI : 0;
        this.chevMat.opacity = warned ? 0.45 + 0.4 * Math.sin(now * 10) : 0;
      }
      if (this.bandMat) {
        const p = warned ? 0.5 + 0.5 * Math.sin(now * 10) : 0;
        this.bandMat.emissive.setRGB(0.23 + p * 0.75, 0.05 + p * 0.25, 0.03 + p * 0.2);
      }
      if (this.cableMesh) this.cableMesh.position.y = Math.sin(now * 1.3) * 0.04 + (gw.stopped ? 0.08 * Math.sin(now * 14) : 0);
    }
    // The winch house: the mill turns, the gears turn their way at their speed, the drum, the lever, the dial.
    const bw = s?.senda?.gearbox;
    const br = s?.rules?.senda?.gearbox;
    if (this.mill) this.mill.rotation.x -= dt * 0.8;
    if (this.B && s) this.drawBox(s, dt, now);
    if (this.leverPull > 0 && this.lever) {
      this.leverPull = Math.max(0, this.leverPull - dt * 0.8);
      this.lever.rotation.z = 0.5 - Math.sin(this.leverPull * Math.PI) * 1.0;
    }
    if (bw && br && this.cableMesh) {
      this.sing = bw.right >= 0 ? Math.min(1, this.sing + dt) : 0;
      this.cableMesh.position.y = this.sing ? Math.sin(now * 46) * 0.03 * this.sing : 0;
    }
    if (this.drum && bw && br) {
      const t = boxTrain(setOf(br.pieces, bw.on));
      const turning = bw.right >= 0 ? br.want * 10 * br.way : 0;
      this.drum.rotation.z -= turning * dt * 0.004;
      if (this.dialNeedle) {
        const now10 = t.full ? (t.speed[3] ?? 0) * (t.dir[3] ?? 0) : 0;
        const want = -dialAngle(now10 / 10) * Math.sign(now10 || 1);
        this.dialNeedle.rotation.z += (want - this.dialNeedle.rotation.z) * Math.min(1, dt * 4);
      }
    }
    // La Picotera's peck coming down, and her wing-beat.
    if (this.peckMark && this.peckMat && bw && s) {
      const on = bw.peckSlot >= -1;
      if (on) {
        const left = Math.max(0, (bw.peck - s.tick) / Math.max(1, br?.boss.peck.warn ?? 36));
        this.peckMark.position.copy(this.ctx.at(bw.peckAt.x, bw.peckAt.y, 0.08));
        this.peckMark.scale.setScalar(0.6 + left * 0.8);
        this.peckMat.opacity = 0.55 + 0.35 * Math.sin(now * 12);
      } else this.peckMat.opacity = 0;
      if (this.beatMat) this.beatMat.opacity = bw.beatAt >= 0 ? 0.35 + 0.35 * Math.sin(now * 12) : 0;
    }
    this.rightFlash = Math.max(0, this.rightFlash - dt * 0.6);
    // Smoke off Grada's chimneys, a puff at a time.
    if (this.smokes.length && now > this.puff) {
      this.puff = now + 0.6;
      const v = this.smokes[Math.floor((now * 7.3) % this.smokes.length)] as THREE.Vector3;
      this.ctx.vfx.burst(this.v3.copy(v).add(this.world.position), { n: 2, color: '#c9ced6', speed: 0.2, up: 0.7, size: 0.7, life: 30, smoke: true });
    }
    this.glow.begin();
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      const x = l.world ? l.p.x + this.world.position.x : l.p.x;
      this.glow.add(x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    if (this.rightFlash > 0 && this.B) {
      const d = this.ctx.at(this.B.drum.x, this.B.drum.y, 1.2);
      this.glow.add(d.x, d.y, d.z, COLD, 3 + 4 * this.rightFlash, this.rightFlash);
    }
    if (bw && bw.peckSlot >= -1) {
      const p = this.ctx.at(bw.peckAt.x, bw.peckAt.y, 0.3);
      this.glow.add(p.x, p.y, p.z, RED, 2.2, 0.5);
    }
    void RED_SOFT;
    this.glow.end();
  }

  /** The gears where they are (shelf, slot, hands), turning their way at their speed; their arrows. */
  private drawBox(s: BattleState, dt: number, now: number): void {
    const r = s.rules?.senda?.gearbox;
    const w = s.senda?.gearbox;
    if (!r || !w) return;
    const t = boxTrain(setOf(r.pieces, w.on));
    const hero = s.heroes[0];
    this.gears.forEach((gv, k) => {
      const at = w.on[k] ?? 5 + k;
      let want: THREE.Vector3;
      if (at < 0 && hero) {
        const hp = this.ctx.pos(hero.id) ?? this.ctx.at(hero.x, hero.y, 0);
        want = this.v3.set(hp.x, hp.y + 2.0, hp.z);
        gv.tilt += (1.2 - gv.tilt) * 0.3;
      } else if (at >= 0 && at < 5) {
        const p = this.slotAt[at] as THREE.Vector3;
        // B2 and C sit a little higher than B1 (so their faces show); R higher still.
        const lift = at === 2 ? 0.16 : at === 3 ? 0.08 : at === 4 ? 0.24 : 0;
        want = this.v3.set(p.x, p.y + 0.62 + lift, p.z);
        gv.tilt += (0 - gv.tilt) * 0.3;
        const dir = t.dir[at] ?? 0;
        const sp = t.speed[at] ?? 0;
        gv.angle -= dir * (sp / 10) * dt * 0.06;
      } else {
        want = this.lieAt(at - 5);
        gv.tilt += (0.9 - gv.tilt) * 0.3;
      }
      gv.pos.lerp(want, at < 0 ? 0.5 : 0.3);
      gv.g.position.copy(gv.pos);
      gv.g.rotation.set(gv.tilt, 0, 0);
      gv.spin.rotation.y = gv.angle;
      gv.g.visible = !(w.right >= 0 && at < 0);
      // Its arrow when it turns: the way, red, over it.
      const arrow = this.arrows[k] as THREE.Mesh;
      const dir = at >= 0 && at < 5 ? (t.dir[at] ?? 0) : 0;
      arrow.visible = dir !== 0;
      if (arrow.visible) {
        const sc = gearR(gv.teeth) / S;
        arrow.position.set(gv.pos.x, gv.pos.y + 0.32, gv.pos.z);
        arrow.scale.set(sc * (dir > 0 ? -1 : 1), 1, sc);
        arrow.rotation.y = now * 0.5 * -dir;
      }
    });
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/* ---------- small things ---------- */

const CYL = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
/** A plain cylinder (shared; the kit copies what it adds). */
const cylOf = (): THREE.BufferGeometry => CYL;

// The crane's sweep (SENDA.crane.swing.r), the car's way and its pylons (SENDA.gondola), read once for the drawing.
const SENDA_SWING_R = SENDA.crane.swing.r;
const SENDA_LEN = SENDA.gondola.len;
const SENDA_PYLONS = SENDA.gondola.pylons.map((t) => t / SENDA.gondola.len);

/** The teeth on each slot from where the gears are (A, B1, B2, C, R). */
const setOf = (pieces: number[], on: number[]): number[] => {
  const set = [0, 0, 0, 0, 0];
  on.forEach((at, k) => {
    if (at >= 0 && at < 5) set[at] = pieces[k] as number;
  });
  return set;
};

/** The dial's needle angle for a speed (0 to 180 a turn a minute over three quarters of the face). */
const dialAngle = (v: number): number => (Math.min(180, Math.abs(v)) / 180) * Math.PI * 0.75;

/** An arrow round a gear (a flat arc with its head), lying on the board. */
const arrowGeo = (): THREE.BufferGeometry => {
  const sh = new THREE.Shape();
  const r0 = 0.95;
  const r1 = 1.12;
  const a0 = 0.2;
  const a1 = Math.PI * 1.3;
  sh.moveTo(Math.cos(a0) * r0, Math.sin(a0) * r0);
  for (let a = a0; a <= a1; a += 0.12) sh.lineTo(Math.cos(a) * r0, Math.sin(a) * r0);
  sh.lineTo(Math.cos(a1) * (r0 - 0.12), Math.sin(a1) * (r0 - 0.12));
  sh.lineTo(Math.cos(a1 + 0.3) * ((r0 + r1) / 2), Math.sin(a1 + 0.3) * ((r0 + r1) / 2));
  sh.lineTo(Math.cos(a1) * (r1 + 0.12), Math.sin(a1) * (r1 + 0.12));
  for (let a = a1; a >= a0; a -= 0.12) sh.lineTo(Math.cos(a) * r1, Math.sin(a) * r1);
  sh.closePath();
  return new THREE.ShapeGeometry(sh).rotateX(-Math.PI / 2);
};

/** A letter or a number painted on a square (the slots' letters, the gears' teeth). */
const letterTex = (text: string, color = '#ff4f3a', bg = 'rgba(12,14,18,0.0)'): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    if (bg !== 'transparent') {
      g.fillStyle = bg;
      g.fillRect(0, 0, 128, 128);
    }
    g.fillStyle = color;
    g.font = `800 ${text.length > 1 ? 68 : 92}px Georgia, serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(text, 64, 70);
  }
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
};

/** Nadd's plan: a cable across an abyss between two faces, a quarry on the far one, the note in the margin. */
const planTex = (): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = 512;
  c.height = 280;
  const g = c.getContext('2d');
  if (g) {
    g.fillStyle = '#d9d3c6';
    g.fillRect(0, 0, 512, 280);
    g.strokeStyle = '#2a3340';
    g.lineWidth = 3;
    // The two faces.
    g.beginPath();
    g.moveTo(40, 40);
    g.lineTo(70, 120);
    g.lineTo(55, 260);
    g.moveTo(470, 30);
    g.lineTo(440, 130);
    g.lineTo(460, 260);
    g.stroke();
    // The cable, sagging, its pylons.
    g.strokeStyle = '#a8301f';
    g.lineWidth = 2.5;
    g.beginPath();
    g.moveTo(70, 70);
    g.quadraticCurveTo(256, 140, 440, 60);
    g.stroke();
    g.strokeStyle = '#2a3340';
    for (const x of [190, 320]) {
      g.beginPath();
      g.moveTo(x, 112);
      g.lineTo(x - 10, 260);
      g.moveTo(x, 112);
      g.lineTo(x + 10, 260);
      g.stroke();
    }
    // The quarry and the crane on the far face; a small car on the line.
    g.strokeRect(410, 46, 26, 14);
    g.fillStyle = '#2a3340';
    g.fillRect(250, 120, 18, 10);
    // The note in the margin: Nadd's hand, a line of it (the words are the scene's, said under it).
    g.strokeStyle = '#3a4250';
    g.lineWidth = 2;
    for (let l = 0; l < 2; l++) {
      g.beginPath();
      for (let x = 120; x < 400 - l * 90; x += 4) g.lineTo(x, 226 + l * 18 + Math.sin(x * 0.37) * 3 + Math.sin(x * 0.11) * 2);
      g.stroke();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
};
